import { ReactNode, useEffect, useRef, useState } from 'react';
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import MapSnapshotButton from 'src/common/components/MapSnapshotButton';
import { admiBuildingsService } from 'src/features/real-estate/admi-buildings.service';
import { Building } from 'src/features/real-estate/buildings.dto';
import { SteckbriefBuildingPickerMap } from './SteckbriefBuildingPickerMap';

/**
 * "Save map image" on the building map (GEN-778) — the Gebäudefoto capture.
 * Present only on hosts that print the photo (the Variant form page).
 */
export interface SteckbriefMapSnapshot {
    /**
     * Receives the captured map canvas (only the selected buildings drawn, no
     * map controls); returns the processed photo for the preview modal, or
     * null when it handled a failure itself.
     */
    onCapture: (canvas: HTMLCanvasElement) => string | null | Promise<string | null>;
    disabled?: boolean;
    /** Status line next to the button (e.g. "the selection changed since the photo"). */
    hint?: ReactNode;
}

export interface SteckbriefBuildingMapPickerProps {
    /** The REA's buildings (`selectedRea.buildings` — ids + codes, no geometry). */
    buildings: { id: string; code: string }[];
    /** Label for a building — the "(1/3) — address" option label shared with the other tabs. */
    buildingLabel: (buildingId: string) => string;
    /** Ids of the buildings currently included in the Steckbrief. */
    selectedIds: Set<string>;
    /** Toggle a building in/out of the selection. */
    onToggle: (buildingId: string) => void;
    /** Blocks toggling while the create request is in flight. */
    disabled?: boolean;
    /**
     * Reports whether the picker renders a map (at least one geometry found)
     * or the full checkbox fallback — lets the dialog pick its width.
     */
    onHasMapChange?: (hasMap: boolean) => void;
    /**
     * Reports every successfully fetched per-building payload (GEN-765) —
     * geometry or not — so hosts can reuse the already-loaded building data
     * (e.g. `buildingFunction` for the Variant form's Gebäudetyp options)
     * without a second round of `GET /v1/buildings/{id}` calls.
     */
    onBuildingDataLoaded?: (buildings: Building[]) => void;
    /**
     * Enables "Save map image" — the Gebäudefoto capture (GEN-778). Only
     * available while the map renders; the checkbox fallback has no camera.
     */
    snapshot?: SteckbriefMapSnapshot;
    /**
     * Hover hint over the selectable area (e.g. "at least one building must
     * remain selected"). Deliberately NOT around the snapshot button row: the
     * button's preview modal is a React descendant, and a wrapping tooltip
     * would reopen above the modal on every mouse move inside it.
     */
    selectionTooltip?: string;
}

/**
 * Building picker for the Steckbrief create dialog (GEN-700): shows ONLY the
 * REA's buildings as clickable footprint polygons on a map. Footprints are
 * fetched per building via `GET /v1/buildings/{id}` when the picker mounts —
 * `selectedRea.buildings` carries ids/codes only, no geometry.
 *
 * Degrades honestly: a building whose geometry fetch fails (or comes back
 * empty) is never silently dropped — it stays selectable through a chip row
 * beneath the map. When NO geometry is available at all, the picker falls
 * back to the previous checkbox-list UI.
 */
export function SteckbriefBuildingMapPicker({
    buildings,
    buildingLabel,
    selectedIds,
    onToggle,
    disabled = false,
    onHasMapChange,
    onBuildingDataLoaded,
    snapshot,
    selectionTooltip,
}: SteckbriefBuildingMapPickerProps) {
    const { t } = useTranslation();
    // The Gebäudefoto camera (GEN-778): the map container the button
    // photographs, and the capture mode the map switches into meanwhile.
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [capturing, setCapturing] = useState(false);

    const [geometryBuildings, setGeometryBuildings] = useState<Building[]>([]);
    const [missingGeometryIds, setMissingGeometryIds] = useState<string[]>([]);
    // The id-list key the current geometry state was loaded for — loading
    // is derived (`key mismatch`) instead of a synchronous setState in the
    // effect body (react-hooks/set-state-in-effect).
    const [loadedForKey, setLoadedForKey] = useState<string | null>(null);

    // Key on the id list, not the array identity — the parent re-creates the
    // dialog content on every render.
    const buildingIdsKey = buildings.map((building) => building.id).join(',');
    const isLoading = loadedForKey !== buildingIdsKey;

    useEffect(() => {
        let cancelled = false;
        // One fetch per REA building — exactly these, nothing around them.
        // `allSettled`: a single failed geometry must not take down the picker;
        // that building degrades to the chip row instead.
        Promise.allSettled(
            buildings.map((building) => admiBuildingsService.loadBuildingData({ uuid: building.id })),
        ).then((results) => {
            if (cancelled) return;
            const withGeometry: Building[] = [];
            const missing: string[] = [];
            const loaded: Building[] = [];
            results.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value != null) {
                    // Selection tracks the REA's building ids — key the
                    // payload by the id we asked for, defensively.
                    loaded.push({ ...result.value, id: buildings[index].id });
                }
                if (result.status === 'fulfilled' && result.value?.geometry != null) {
                    withGeometry.push({ ...result.value, id: buildings[index].id });
                } else {
                    missing.push(buildings[index].id);
                }
            });
            setGeometryBuildings(withGeometry);
            setMissingGeometryIds(missing);
            setLoadedForKey(buildingIdsKey);
            onHasMapChange?.(withGeometry.length > 0);
            onBuildingDataLoaded?.(loaded);
        });
        return () => {
            cancelled = true;
        };
    }, [buildingIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    // Full fallback: no geometry anywhere — the previous checkbox-list UI.
    if (geometryBuildings.length === 0) {
        return (
            <Tooltip title={selectionTooltip ?? ''} placement="top-start">
                <FormGroup>
                    {buildings.map((building) => (
                        <FormControlLabel
                            key={building.id}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedIds.has(building.id)}
                                    onChange={() => onToggle(building.id)}
                                    disabled={disabled}
                                />
                            }
                            label={<Typography variant="body2">{buildingLabel(building.id)}</Typography>}
                        />
                    ))}
                </FormGroup>
            </Tooltip>
        );
    }

    const selectedCount = buildings.filter((building) => selectedIds.has(building.id)).length;

    return (
        <Stack spacing={1}>
            {snapshot && (
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <MapSnapshotButton
                        targetRef={mapContainerRef}
                        label={t('Save map image')}
                        disabled={snapshot.disabled}
                        onBeforeCapture={() => setCapturing(true)}
                        onAfterCapture={() => setCapturing(false)}
                        onCapture={snapshot.onCapture}
                    />
                    {snapshot.hint}
                </Stack>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('Click on buildings to remove them from this Steckbrief.')}
            </Typography>
            <Tooltip title={selectionTooltip ?? ''} placement="top-start">
                <Box>
                    <SteckbriefBuildingPickerMap
                        buildings={geometryBuildings}
                        selectedIds={selectedIds}
                        onToggle={onToggle}
                        buildingLabel={buildingLabel}
                        disabled={disabled}
                        camera={snapshot ? { containerRef: mapContainerRef, capturing } : undefined}
                    />
                </Box>
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('{{selected}} of {{total}} buildings selected', {
                    selected: selectedCount,
                    total: buildings.length,
                })}
            </Typography>
            {missingGeometryIds.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        {t('Buildings without map geometry — select them here:')}
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {missingGeometryIds.map((buildingId) => {
                            const selected = selectedIds.has(buildingId);
                            return (
                                <Chip
                                    key={buildingId}
                                    size="small"
                                    label={buildingLabel(buildingId)}
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                    onClick={() => onToggle(buildingId)}
                                    disabled={disabled}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}
